import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useHits, useRange } from 'react-instantsearch-hooks-web';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import { FacetProps } from './types';
import { useProductList } from '../../context';
import { refinementRemovedEventName, refinementsClearedEventName } from '../../context/constants';
import { RefinementRemovedEvent } from '../../context/types';
import { OUTLET_LENGTH } from 'helpers/constants/attributes';

const RangeFacet: React.FC<FacetProps> = ({ attribute }) => {
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { locale } = useParams();

  const { numericRanges, pricesConfiguration, updateNumericRange } = useProductList();

  const { results } = useHits();

  const disjunctiveFacet = useMemo(
    () => results?.disjunctiveFacets.find((facet) => facet.name === attribute),
    [attribute, results],
  );

  const { range, refine, start, canRefine } = useRange({ attribute });

  const { currency, currencySymbol } = useI18n();

  const configuration = useMemo(() => pricesConfiguration[attribute], [pricesConfiguration, attribute]);
  const [appliedOptions, setAppliedOptions] = useState<Array<number>>([]);
  const [priceRange, setPriceRange] = useState({ min: -Infinity, max: Infinity });

  useEffect(() => {
    setPriceRange((priceRange) => {
      const min = start[0] && start[0] !== -Infinity ? start[0] / 100 : priceRange.min;
      const max = start[1] && start[1] !== Infinity ? start[1] / 100 : priceRange.max;
      return { min, max };
    });
  }, [start]);

  const applyRefinement = useCallback(
    (appliedRange: typeof priceRange) => {
      const min = Math.max(range.min as number, appliedRange.min);
      const max = Math.min(range.max as number, appliedRange.max);

      updateNumericRange(attribute, [appliedRange.min, appliedRange.max]);

      refine([min, max]);
    },
    [range, refine],
  );

  const handleRangeOptionChange = useCallback(
    (index: number, checked: boolean) => {
      const newAppliedOptions = checked
        ? [...appliedOptions, index]
        : appliedOptions.filter((option) => option !== index);

      const appliedRanges = newAppliedOptions.map((index) => ranges[index]);

      const appliedRange =
        appliedRanges.length > 0
          ? appliedRanges.reduce(
              (acc, { min, max }) => ({
                min: Math.min(min, acc.min),
                max: Math.max(max, acc.max),
              }),
              { min: Infinity, max: -Infinity },
            )
          : { min: -Infinity, max: Infinity };

      applyRefinement({ min: appliedRange.min, max: appliedRange.max });

      setAppliedOptions(newAppliedOptions);

      setPriceRange({ ...appliedRange });
    },
    [appliedOptions, configuration, applyRefinement],
  );

  const clearAppliedOptions = useCallback(() => {
    setAppliedOptions([]);
    updateNumericRange(attribute, [-1, 1]);

  }, []);

  //   useEffect(() => {
  //     clearAppliedOptions();
  //   }, [range.min, range.max, clearAppliedOptions]);

  const reset = useCallback(() => {
    clearAppliedOptions();
    setPriceRange({ min: -Infinity, max: Infinity });
  }, [clearAppliedOptions]);

  const handleRefinementRemoved = useCallback(
    (e: CustomEvent<RefinementRemovedEvent>) => {
      if (e.detail.attribute === attribute) reset();
    },
    [reset, attribute],
  );

  useEffect(() => {
    window.addEventListener(refinementRemovedEventName, handleRefinementRemoved as EventListener);
    window.addEventListener(refinementsClearedEventName, reset);

    return () => {
      window.removeEventListener(refinementRemovedEventName, handleRefinementRemoved as EventListener);
      window.removeEventListener(refinementsClearedEventName, reset);
    };
  }, [reset, handleRefinementRemoved]);

  const ranges =
    configuration &&
    configuration?.ranges
      .map((range) => {
        if (!disjunctiveFacet) return range;

        const refinements = Object.entries(disjunctiveFacet.data).reduce((acc, [value, count]) => {
          const amount = +value;
          if (amount >= range.min && amount <= range.max) {
            return acc + count;
          }
          return acc;
        }, 0);

        return { ...range, refinements };
      })
      .filter(({ refinements }) => refinements && refinements > 0);

  const rangeOptions = useMemo(() => {
    if (!configuration) return { available: false, Component: <></> };
    return {
      available: ranges.length > 0,
      Component: ranges.map(({ min, max }, index) => (
        <div key={index} className="flex items-center justify-start gap-12 hover:cursor-pointer">
          <div className="flex items-center gap-12">
            <Checkbox
              checked={
                appliedOptions.includes(index) ? appliedOptions.includes(index) : numericRanges?.[attribute]?.[0] === min
              }
              onChange={({ checked }) => handleRangeOptionChange(index, checked)}
              labelPosition="on-left"
            />
          </div>
          {attribute.split('.').pop() === OUTLET_LENGTH ? (
            <div>
              {min + 'mm'} - {max + 'mm'}
            </div>
          ) : (
            <div>
              {min} - {max}
            </div>
          )}
        </div>
      )),
    };
  }, [configuration, handleRangeOptionChange, currency, disjunctiveFacet, appliedOptions, locale]);

  return (
    <div>
      {rangeOptions.available && (
        <div className="flex flex-col gap-18 px-2 py-16 lg:min-w-[20rem]">{rangeOptions.Component}</div>
      )}
    </div>
  );
};

export default RangeFacet;
